import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import API from '../api/axios';
import {
  FiPlus, FiDownload, FiEye, FiEdit2, FiTrash2, FiX,
  FiShoppingCart, FiPackage, FiCheckCircle, FiCheck,
  FiSearch, FiFilter, FiRefreshCw, FiChevronDown, FiChevronUp, FiCalendar,
  FiPhone, FiMail, FiMapPin, FiMoreVertical, FiTrendingUp,
  FiBarChart2, FiUsers,
} from 'react-icons/fi';
import './PurchaseManagement.css';

/* ── helpers ── */
const fmt = iso => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtINR = n => '₹ ' + Number(n || 0).toLocaleString('en-IN');
const poValue = o => (o.items || []).reduce((s, i) => s + (i.orderedQty || 0) * (i.rate || 0), 0);
const poReceived = o => (o.items || []).reduce((s, i) => s + (i.receivedQty || 0) * (i.rate || 0), 0);
const poPending = o => poValue(o) - poReceived(o);

function daysDiff(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date() - new Date(dateStr)) / 86400000);
  return diff;
}

const PO_STATUS = {
  draft:     { label: 'Draft',              color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
  sent:      { label: 'Ordered',            color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  partial:   { label: 'Partially Received', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  received:  { label: 'Fully Received',     color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  cancelled: { label: 'Cancelled',          color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

const VENDOR_STATUS = {
  active:   { label: 'Active',   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  inactive: { label: 'Inactive', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

function Badge({ status, config }) {
  const cfg = config[status] || config[Object.keys(config)[0]];
  return (
    <span className="vmr-badge" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
}

/* ── vendor-level stats from orders ── */
function vendorStats(vendor, orders) {
  const vOrders = orders.filter(o => (o.vendor?._id || o.vendor) === vendor._id);
  const totalOrdered  = vOrders.reduce((s, o) => s + poValue(o), 0);
  const totalReceived = vOrders.reduce((s, o) => s + poReceived(o), 0);
  const totalPending  = totalOrdered - totalReceived;
  const fullyRec  = vOrders.filter(o => o.status === 'received').length;
  const partial   = vOrders.filter(o => o.status === 'partial').length;
  const ordered   = vOrders.filter(o => o.status === 'sent').length;
  const delayed   = vOrders.filter(o => o.status === 'sent' && o.expectedDate && new Date() > new Date(o.expectedDate));
  const onTime    = vOrders.filter(o => o.status === 'received');
  const avgDelay  = delayed.length
    ? (delayed.reduce((s, o) => s + daysDiff(o.expectedDate), 0) / delayed.length).toFixed(1)
    : 0;
  const onTimePct = vOrders.length ? Math.round((onTime.length / vOrders.length) * 100) : 0;
  return { vOrders, totalOrdered, totalReceived, totalPending, fullyRec, partial, ordered, delayed: delayed.length, avgDelay, onTimePct };
}

/* ── mini bar for status summary ── */
function StatusBar({ ordered, partial, fullyRec, total }) {
  if (!total) return <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No POs</span>;
  const ordPct  = Math.round((ordered   / total) * 100);
  const partPct = Math.round((partial   / total) * 100);
  const recPct  = Math.round((fullyRec  / total) * 100);
  return (
    <div className="vmr-status-bar-wrap">
      <div className="vmr-status-chips">
        {ordered  > 0 && <span className="vmr-chip vmr-chip-orange">{ordered} ({ordPct}%)</span>}
        {partial  > 0 && <span className="vmr-chip vmr-chip-blue">{partial} ({partPct}%)</span>}
        {fullyRec > 0 && <span className="vmr-chip vmr-chip-green">{fullyRec} ({recPct}%)</span>}
      </div>
      <div className="vmr-mini-bar">
        <div style={{ width: `${recPct}%`,  background: '#16a34a', height: '100%' }} />
        <div style={{ width: `${partPct}%`, background: '#2563eb', height: '100%' }} />
        <div style={{ width: `${ordPct}%`,  background: '#f59e0b', height: '100%' }} />
      </div>
    </div>
  );
}

export default function PurchaseManagement() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('tracking');
  const [vendors, setVendors]   = useState([]);
  const [orders, setOrders]     = useState([]);
  const [projects, setProjects] = useState([]);
  const [plannedProjectIds, setPlannedProjectIds] = useState(new Set());
  const [filterProject, setFilterProject] = useState('');
  const [poSearch, setPoSearch]           = useState('');
  const [poStatusFilter, setPoStatusFilter] = useState('');

  // modals
  const [showPOModal,     setShowPOModal]     = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [editOrder,  setEditOrder]  = useState(null);
  const [viewOrder,  setViewOrder]  = useState(null);
  const [confirm,    setConfirm]    = useState(null);
  const [loading,    setLoading]    = useState(true);

  // tracking tab state
  const [trackSearch,  setTrackSearch]  = useState('');
  const [expandedRow,  setExpandedRow]  = useState(null);
  const [trackSortKey, setTrackSortKey] = useState('pending');

  // vendor tab state
  const [vendorSearch,  setVendorSearch]  = useState('');
  const [vendorFilter,  setVendorFilter]  = useState('');
  const [expandedVendor, setExpandedVendor] = useState(null);

  const loadAll = useCallback(() => {
    Promise.all([
      API.get('/purchase/vendors').then(r => setVendors(r.data)).catch(() => {}),
      API.get('/purchase/orders').then(r => setOrders(r.data)).catch(() => {}),
      API.get('/projects').then(r => {
        setProjects(r.data);
        const pid = new URLSearchParams(location.search).get('projectId');
        if (pid) setFilterProject(pid);
      }).catch(() => {}),
      API.get('/material-planning').then(r => {
        setPlannedProjectIds(new Set((r.data || []).map(id => String(id))));
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── project-filtered orders ── */
  const filteredOrders = filterProject
    ? orders.filter(o => (o.project?._id || o.project) === filterProject)
    : orders;

  /* ── global stats ── */
  const totalPO      = filteredOrders.length;
  const totalPOVal   = filteredOrders.reduce((s, o) => s + poValue(o), 0);
  const fullyRec     = filteredOrders.filter(o => o.status === 'received');
  const partialRec   = filteredOrders.filter(o => o.status === 'partial');
  const orderedPOs   = filteredOrders.filter(o => o.status === 'sent');

  const statCards = [
    { label: 'Total POs',             value: totalPO,          sub: `Value: ${fmtINR(totalPOVal)}`,                                         icon: <FiShoppingCart />, color: '#2563eb', bg: '#eff6ff' },
    { label: 'Fully Received',        value: fullyRec.length,  sub: `${Math.round(fullyRec.length/Math.max(totalPO,1)*100)}% | Value: ${fmtINR(fullyRec.reduce((s,o)=>s+poValue(o),0))}`,  icon: <FiCheckCircle  />, color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Partially Received',    value: partialRec.length,sub: `${Math.round(partialRec.length/Math.max(totalPO,1)*100)}% | Value: ${fmtINR(partialRec.reduce((s,o)=>s+poPending(o),0))}`, icon: <FiPackage />, color: '#f59e0b', bg: '#fffbeb' },
    { label: 'Ordered (Not Received)',value: orderedPOs.length, sub: `${Math.round(orderedPOs.length/Math.max(totalPO,1)*100)}% | Value: ${fmtINR(orderedPOs.reduce((s,o)=>s+poValue(o),0))}`, icon: <FiShoppingCart />, color: '#64748b', bg: '#f1f5f9' },
  ];

  /* ── vendor-wise table rows ── */
  const vendorRows = vendors.map(v => {
    const s = vendorStats(v, filteredOrders);
    const lastOrder = filteredOrders.filter(o => (o.vendor?._id || o.vendor) === v._id).sort((a,b) => new Date(b.orderDate) - new Date(a.orderDate))[0];
    return { ...v, ...s, lastOrder };
  }).filter(v => {
    const q = trackSearch.toLowerCase();
    return !q || v.name?.toLowerCase().includes(q) || v.vOrders?.some(o => o.poNumber?.toLowerCase().includes(q));
  }).sort((a, b) => {
    if (trackSortKey === 'pending')  return b.totalPending - a.totalPending;
    if (trackSortKey === 'delayed')  return b.delayed - a.delayed;
    if (trackSortKey === 'ontime')   return b.onTimePct - a.onTimePct;
    return b.vOrders?.length - a.vOrders?.length;
  });

  /* ── delayed POs for follow-up center ── */
  const followUpPOs = orders
    .filter(o => ['sent','partial'].includes(o.status) && o.expectedDate && new Date() > new Date(o.expectedDate))
    .map(o => ({ ...o, delayDays: daysDiff(o.expectedDate), pendingItems: (o.items||[]).filter(i => (i.receivedQty||0) < i.orderedQty).length }))
    .sort((a,b) => b.delayDays - a.delayDays)
    .slice(0, 5);

  /* ── top delayed POs for chart section ── */
  const topDelayed = [...orders]
    .filter(o => o.expectedDate && (o.status === 'sent' || o.status === 'partial'))
    .map(o => ({ ...o, delayDays: Math.max(0, daysDiff(o.expectedDate)), pendingVal: poPending(o) }))
    .sort((a,b) => b.delayDays - a.delayDays)
    .slice(0, 5);

  /* ── vendor list for management tab ── */
  const filteredVendors = vendors.filter(v => {
    const q = vendorSearch.toLowerCase();
    const matchSearch = !q || v.name?.toLowerCase().includes(q) || v.contactPerson?.toLowerCase().includes(q) || v.category?.toLowerCase().includes(q);
    const matchFilter = !vendorFilter || v.status === vendorFilter;
    return matchSearch && matchFilter;
  });

  async function doConfirm() {
    const { type, item } = confirm; setConfirm(null);
    try {
      if (type === 'vendor') {
        await API.delete(`/purchase/vendors/${item._id}`);
        setVendors(v => v.filter(x => x._id !== item._id));
        toast.success('Vendor deleted');
      } else {
        await API.delete(`/purchase/orders/${item._id}`);
        setOrders(o => o.filter(x => x._id !== item._id));
        toast.success('PO deleted');
      }
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <Layout>
      <>
      {loading && <PageLoader text="Loading purchase data..." />}
      <div className="page vmr-page">
      <div className="vmr-root">

        {/* ── Top action bar ── */}
        <div className="page-actions vmr-action-bar">
          <div className="vmr-tabs">
            <button className={`vmr-tab ${activeTab === 'tracking' ? 'vmr-tab--active' : ''}`} onClick={() => setActiveTab('tracking')}>
              <FiBarChart2 size={13} /> Material Receipt Tracking
            </button>
            <button className={`vmr-tab ${activeTab === 'vendors' ? 'vmr-tab--active' : ''}`} onClick={() => setActiveTab('vendors')}>
              <FiUsers size={13} /> Vendor Management
            </button>
            <button className={`vmr-tab ${activeTab === 'orders' ? 'vmr-tab--active' : ''}`} onClick={() => setActiveTab('orders')}>
              <FiShoppingCart size={13} /> Purchase Orders
            </button>
          </div>
          <div className="vmr-action-btns">
            <button className="vmr-btn-ghost" onClick={loadAll} title="Refresh"><FiRefreshCw size={13} /></button>
            <button className="vmr-btn-ghost" onClick={() => { setEditOrder(null); setShowPOModal(true); }}><FiPlus size={13} /> <span>New PO</span></button>
            <button className="vmr-btn-primary" onClick={() => { setEditVendor(null); setShowVendorModal(true); }}><FiPlus size={13} /> <span>Add Vendor</span></button>
          </div>
        </div>

        {/* ── Project filter banner ── */}
        <div className="vmr-proj-filter">
          <FiFilter size={13} style={{ color: '#64748b', flexShrink: 0 }} />
          <ProjectSearchSelect
            projects={projects}
            value={filterProject}
            onChange={p => setFilterProject(p ? p._id : '')}
            allOption={true}
            placeholder="All Projects"
          />
          {filterProject && (
            <button className="vmr-proj-clear" onClick={() => setFilterProject('')} title="Clear filter">
              <FiX size={12} /> Clear
            </button>
          )}
        </div>

        {/* ── Stat cards ── */}
        <div className="vmr-stat-row">
          {statCards.map((s, i) => (
            <div key={i} className="vmr-stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
              <div className="vmr-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
              <div className="vmr-stat-body">
                <p className="vmr-stat-label">{s.label}</p>
                <p className="vmr-stat-num" style={{ color: s.color }}>{s.value}</p>
                <p className="vmr-stat-sub">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ══ TAB: Material Receipt Tracking ══ */}
        {activeTab === 'tracking' && (
          <div className="vmr-tracking-layout">

            {/* Main table + followup side by side */}
            <div className="vmr-main-col">
              <div className="vmr-card">
                <div className="vmr-card-header">
                  <h3>Vendor-Wise Material Receipt Status</h3>
                  <div className="vmr-toolbar">
                    <div className="vmr-search">
                      <FiSearch size={13} />
                      <input placeholder="Search Vendor / Material / PO No." value={trackSearch} onChange={e => setTrackSearch(e.target.value)} />
                    </div>
                    <select className="vmr-select" value={trackSortKey} onChange={e => setTrackSortKey(e.target.value)}>
                      <option value="pending">Sort: Most Pending</option>
                      <option value="delayed">Sort: Most Delayed</option>
                      <option value="ontime">Sort: On-Time %</option>
                      <option value="pos">Sort: PO Count</option>
                    </select>
                    <button className="vmr-btn-ghost vmr-sm" onClick={() => {
                      const rows = [['Vendor','Category','POs','Ordered (₹)','Received (₹)','Pending (₹)','On-Time %','Avg Delay (days)']];
                      vendorRows.forEach(v => rows.push([v.name, v.category||'—', v.totalOrders||0, v.totalOrdered||0, v.totalReceived||0, v.totalPending||0, v.onTimePct||0, v.avgDelay||0]));
                      const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
                      const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                      a.download = 'vendor-receipt-tracking.csv'; a.click();
                    }}><FiDownload size={13} /> Export</button>
                  </div>
                </div>
                <div className="vmr-table-wrap">
                  <table className="vmr-table">
                    <thead>
                      <tr>
                        <th>Vendor Name</th>
                        <th className="vmr-th-c">POs</th>
                        <th>Ordered Value (₹)</th>
                        <th>Received Value (₹)</th>
                        <th>Pending Value (₹)</th>
                        <th className="vmr-hide-md">Status Summary</th>
                        <th className="vmr-th-c">On-Time %</th>
                        <th className="vmr-th-c vmr-hide-md">Avg. Delay (Days)</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorRows.length === 0 && (
                        <tr><td colSpan={9} className="vmr-empty">No vendor data found</td></tr>
                      )}
                      {vendorRows.map(v => (
                        <React.Fragment key={v._id}>
                          <tr className={`vmr-tr ${expandedRow === v._id ? 'vmr-tr--expanded' : ''}`}>
                            <td>
                              <div className="vmr-vendor-name-cell">
                                <div className="vmr-vendor-avatar" style={{ background: strColor(v.name) }}>
                                  {v.name?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                  <p className="vmr-vname">{v.name}</p>
                                  {v.category && <p className="vmr-vcategory">{v.category}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="vmr-td-c vmr-td-bold">{v.vOrders?.length || 0}</td>
                            <td className="vmr-td-val">{fmtINR(v.totalOrdered)}</td>
                            <td className="vmr-td-val vmr-green">{fmtINR(v.totalReceived)}</td>
                            <td className="vmr-td-val vmr-orange">{fmtINR(v.totalPending)}</td>
                            <td className="vmr-hide-md">
                              <StatusBar
                                ordered={v.ordered} partial={v.partial} fullyRec={v.fullyRec}
                                total={v.vOrders?.length || 0}
                              />
                            </td>
                            <td className="vmr-td-c">
                              <span className={`vmr-pct ${v.onTimePct >= 80 ? 'vmr-pct-green' : v.onTimePct >= 50 ? 'vmr-pct-orange' : 'vmr-pct-red'}`}>
                                {v.onTimePct}%
                              </span>
                            </td>
                            <td className="vmr-td-c vmr-hide-md">
                              <span className={`vmr-delay ${Number(v.avgDelay) > 3 ? 'vmr-delay-red' : Number(v.avgDelay) > 1 ? 'vmr-delay-orange' : 'vmr-delay-green'}`}>
                                {v.avgDelay}
                              </span>
                            </td>
                            <td>
                              <div className="vmr-row-actions">
                                <button className="vmr-act-btn" onClick={() => setExpandedRow(expandedRow === v._id ? null : v._id)} title="View Details">
                                  View Details {expandedRow === v._id ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />}
                                </button>
                                <button className="vmr-icon-btn" onClick={() => { setEditVendor(v); setShowVendorModal(true); }}><FiMoreVertical size={13} /></button>
                              </div>
                            </td>
                          </tr>
                          {expandedRow === v._id && (
                            <tr className="vmr-expand-row">
                              <td colSpan={9}>
                                <div className="vmr-expand-panel">
                                  <div className="vmr-expand-header">
                                    <span>Purchase Orders for <strong>{v.name}</strong></span>
                                    <button className="vmr-btn-ghost vmr-sm" onClick={() => { setEditOrder(null); setShowPOModal(true); }}>
                                      <FiPlus size={11} /> New PO
                                    </button>
                                  </div>
                                  <table className="vmr-sub-table">
                                    <thead>
                                      <tr>
                                        <th>PO No.</th><th>Project</th><th>PO Date</th>
                                        <th>Expected</th><th>PO Value</th><th>Received</th><th>Pending</th><th>Status</th><th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {v.vOrders?.length === 0 && <tr><td colSpan={9} className="vmr-empty">No POs yet</td></tr>}
                                      {v.vOrders?.map(o => (
                                        <tr key={o._id}>
                                          <td className="vmr-td-mono">{o.poNumber}</td>
                                          <td>{o.project?.title || '—'}</td>
                                          <td>{fmt(o.orderDate)}</td>
                                          <td className={o.expectedDate && new Date() > new Date(o.expectedDate) && o.status !== 'received' ? 'vmr-red' : ''}>
                                            {fmt(o.expectedDate)}
                                          </td>
                                          <td>{fmtINR(poValue(o))}</td>
                                          <td className="vmr-green">{fmtINR(poReceived(o))}</td>
                                          <td className="vmr-orange">{fmtINR(poPending(o))}</td>
                                          <td><Badge status={o.status} config={PO_STATUS} /></td>
                                          <td>
                                            <div className="vmr-row-actions">
                                              <button className="vmr-icon-btn" onClick={() => setViewOrder(o)}><FiEye size={12} /></button>
                                              <button className="vmr-icon-btn" onClick={() => { setEditOrder(o); setShowPOModal(true); }}><FiEdit2 size={12} /></button>
                                              <button className="vmr-icon-btn vmr-del" onClick={() => setConfirm({ type: 'order', item: o })}><FiTrash2 size={12} /></button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Bottom charts row ── */}
              <div className="vmr-charts-row">
                {/* Receipt status donut */}
                <div className="vmr-card vmr-chart-card">
                  <h4>Material Receipt Status (Overall)</h4>
                  <DonutChart orders={orders} />
                  <div className="vmr-tip-box">
                    <FiPackage size={13} />
                    <span>Better material visibility helps in improved project planning and reduces risk of production delays.</span>
                  </div>
                </div>

                {/* Top delayed POs */}
                <div className="vmr-card vmr-chart-card">
                  <div className="vmr-card-header">
                    <h4>Top Delayed POs</h4>
                  </div>
                  <table className="vmr-sub-table">
                    <thead>
                      <tr><th>PO No.</th><th>Vendor</th><th>Delay (Days)</th><th>Pending Value (₹)</th></tr>
                    </thead>
                    <tbody>
                      {topDelayed.length === 0 && <tr><td colSpan={4} className="vmr-empty">No delayed POs</td></tr>}
                      {topDelayed.map(o => (
                        <tr key={o._id}>
                          <td className="vmr-td-mono">{o.poNumber}</td>
                          <td>{o.vendor?.name || '—'}</td>
                          <td><span className="vmr-delay-badge vmr-red">{o.delayDays}</span></td>
                          <td className="vmr-orange">{fmtINR(o.pendingVal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {topDelayed.length > 0 && (
                    <div className="vmr-view-all">View All Delayed POs →</div>
                  )}
                </div>

                {/* Vendor performance chart */}
                <div className="vmr-card vmr-chart-card">
                  <h4>Procurement Performance (Vendor-wise)</h4>
                  <VendorPerfChart vendors={vendors} orders={orders} />
                  <div className="vmr-tip-box vmr-tip-green">
                    <FiTrendingUp size={13} />
                    <span>Track vendor performance to ensure on-time delivery and reduce material shortages.</span>
                  </div>
                </div>
              </div>

            </div>

            {/* ── Follow-up strip (only shown when there are delayed POs) ── */}
            {followUpPOs.length > 0 && (
              <div className="vmr-card vmr-followup-strip">
                <div className="vmr-card-header">
                  <h3>Vendor Follow-up Center</h3>
                  <span className="vmr-followup-count">{followUpPOs.length} Delayed PO{followUpPOs.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="vmr-followup-list">
                  {followUpPOs.map(o => (
                    <div key={o._id} className="vmr-followup-item">
                      <div className="vmr-vendor-avatar vmr-avatar-sm" style={{ background: strColor(o.vendor?.name || '') }}>
                        {o.vendor?.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="vmr-followup-info">
                        <span className="vmr-followup-vname">{o.vendor?.name || '—'}</span>
                        <span className="vmr-followup-po">PO: {o.poNumber} · {fmtINR(poPending(o))} pending</span>
                      </div>
                      <span className="vmr-delay-tag">+{o.delayDays}d late</span>
                      <button className="vmr-followup-btn" onClick={() => setViewOrder(o)}>Follow Up</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {/* ══ TAB: Vendor Management ══ */}
        {activeTab === 'vendors' && (
          <div>
            <div className="vmr-card">
              <div className="vmr-card-header">
                <h3>Vendor Management</h3>
                <div className="vmr-toolbar">
                  <div className="vmr-search">
                    <FiSearch size={13} />
                    <input placeholder="Search vendor, contact, category..." value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} />
                  </div>
                  <select className="vmr-select" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <button className="vmr-btn-primary vmr-sm" onClick={() => { setEditVendor(null); setShowVendorModal(true); }}>
                    <FiPlus size={12} /> Add Vendor
                  </button>
                </div>
              </div>
              <div className="vmr-vendor-grid">
                {filteredVendors.length === 0 && (
                  <div className="vmr-empty-state" style={{ gridColumn: '1/-1', padding: '3rem' }}>
                    <FiUsers size={32} color="#94a3b8" />
                    <p style={{ margin: '0.5rem 0 0', color: '#334155', fontWeight: 600 }}>No vendors found</p>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.82rem' }}>Add a vendor to get started</p>
                  </div>
                )}
                {filteredVendors.map(v => {
                  const s = vendorStats(v, orders);
                  const isExp = expandedVendor === v._id;
                  return (
                    <div key={v._id} className="vmr-vendor-card">
                      <div className="vmr-vc-header">
                        <div className="vmr-vendor-avatar vmr-avatar-lg" style={{ background: strColor(v.name) }}>
                          {v.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="vmr-vc-info">
                          <p className="vmr-vc-name">{v.name}</p>
                          {v.category && <p className="vmr-vc-category">{v.category}</p>}
                        </div>
                        <div className="vmr-vc-status">
                          <Badge status={v.status || 'active'} config={VENDOR_STATUS} />
                        </div>
                      </div>
                      <div className="vmr-vc-meta">
                        {v.contactPerson && <div className="vmr-vc-row"><FiUsers size={12} /><span>{v.contactPerson}</span></div>}
                        {v.phone && <div className="vmr-vc-row"><FiPhone size={12} /><span>{v.phone}</span></div>}
                        {v.email && <div className="vmr-vc-row"><FiMail size={12} /><span>{v.email}</span></div>}
                        {v.address && <div className="vmr-vc-row"><FiMapPin size={12} /><span>{v.address}</span></div>}
                        {v.gstin && <div className="vmr-vc-row"><span className="vmr-vc-label">GSTIN</span><span className="vmr-td-mono">{v.gstin}</span></div>}
                      </div>
                      <div className="vmr-vc-stats">
                        <div className="vmr-vc-stat">
                          <p className="vmr-vc-stat-num">{s.vOrders?.length || 0}</p>
                          <p className="vmr-vc-stat-label">Total POs</p>
                        </div>
                        <div className="vmr-vc-stat">
                          <p className="vmr-vc-stat-num vmr-orange">{s.delayed}</p>
                          <p className="vmr-vc-stat-label">Delayed</p>
                        </div>
                        <div className="vmr-vc-stat">
                          <p className="vmr-vc-stat-num vmr-green">{s.onTimePct}%</p>
                          <p className="vmr-vc-stat-label">On-Time</p>
                        </div>
                        <div className="vmr-vc-stat">
                          <p className="vmr-vc-stat-num">{fmtINR(s.totalPending)}</p>
                          <p className="vmr-vc-stat-label">Pending</p>
                        </div>
                      </div>
                      <div className="vmr-vc-footer">
                        <button className="vmr-vc-btn-ghost" onClick={() => setExpandedVendor(isExp ? null : v._id)}>
                          {isExp ? 'Hide POs' : 'View POs'} {isExp ? <FiChevronUp size={11}/> : <FiChevronDown size={11}/>}
                        </button>
                        <button className="vmr-vc-btn-ghost" onClick={() => { setEditVendor(v); setShowVendorModal(true); }}><FiEdit2 size={12}/></button>
                        <button className="vmr-vc-btn-ghost vmr-del" onClick={() => setConfirm({ type: 'vendor', item: v })}><FiTrash2 size={12}/></button>
                      </div>
                      {isExp && (
                        <div className="vmr-vc-orders">
                          <table className="vmr-sub-table">
                            <thead><tr><th>PO No.</th><th>Date</th><th>Value</th><th>Status</th><th></th></tr></thead>
                            <tbody>
                              {s.vOrders?.length === 0 && <tr><td colSpan={5} className="vmr-empty">No POs</td></tr>}
                              {s.vOrders?.map(o => (
                                <tr key={o._id}>
                                  <td className="vmr-td-mono">{o.poNumber}</td>
                                  <td>{fmt(o.orderDate)}</td>
                                  <td>{fmtINR(poValue(o))}</td>
                                  <td><Badge status={o.status} config={PO_STATUS} /></td>
                                  <td>
                                    <button className="vmr-icon-btn" onClick={() => setViewOrder(o)}><FiEye size={11}/></button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ══ TAB: Purchase Orders ══ */}
        {activeTab === 'orders' && (
          <div className="vmr-po-page">
            {/* Header bar */}
            <div className="vmr-po-topbar">
              <div className="vmr-po-topbar-left">
                <h3 className="vmr-po-heading">Purchase Orders</h3>
                <span className="vmr-po-count">{filteredOrders.length} orders</span>
              </div>
              <div className="vmr-po-topbar-right">
                <div className="vmr-po-search">
                  <FiSearch size={13} />
                  <input placeholder="Search PO / Vendor / Project…" value={poSearch} onChange={e => setPoSearch(e.target.value)} />
                </div>
                <select className="vmr-po-filter-sel" value={poStatusFilter} onChange={e => setPoStatusFilter(e.target.value)}>
                  <option value="">All Status</option>
                  {Object.entries(PO_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button className="vmr-btn-primary" onClick={() => { setEditOrder(null); setShowPOModal(true); }}>
                  <FiPlus size={13} /> New PO
                </button>
              </div>
            </div>

            {/* PO cards */}
            {(() => {
              const list = filteredOrders.filter(o => {
                const q = poSearch.toLowerCase();
                const matchQ = !q || o.poNumber?.toLowerCase().includes(q) || o.vendor?.name?.toLowerCase().includes(q) || o.project?.title?.toLowerCase().includes(q);
                const matchS = !poStatusFilter || o.status === poStatusFilter;
                return matchQ && matchS;
              });
              if (!list.length) return (
                <div className="vmr-po-empty">
                  <FiShoppingCart size={36} color="#cbd5e1" />
                  <p>No purchase orders found</p>
                  <button className="vmr-btn-primary" onClick={() => { setEditOrder(null); setShowPOModal(true); }}><FiPlus size={13}/> Create First PO</button>
                </div>
              );
              return (
                <div className="vmr-po-grid">
                  {list.map(o => {
                    const val     = poValue(o);
                    const recv    = poReceived(o);
                    const pend    = poPending(o);
                    const pct     = val > 0 ? Math.round(recv / val * 100) : 0;
                    const isDelayed = ['sent','partial'].includes(o.status) && o.expectedDate && new Date() > new Date(o.expectedDate);
                    const cfg     = PO_STATUS[o.status] || PO_STATUS.draft;
                    return (
                      <div key={o._id} className={`vmr-po-card ${isDelayed ? 'vmr-po-card-delayed' : ''}`}>
                        {/* Card top */}
                        <div className="vmr-po-card-head">
                          <div className="vmr-po-card-id">
                            <span className="vmr-po-num">{o.poNumber}</span>
                            <span className="vmr-po-badge" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>{cfg.label}</span>
                            {isDelayed && <span className="vmr-po-delayed-tag">Delayed</span>}
                          </div>
                          <div className="vmr-po-card-actions">
                            <button className="vmr-icon-btn" title="View" onClick={() => setViewOrder(o)}><FiEye size={13}/></button>
                            <button className="vmr-icon-btn" title="Edit" onClick={() => { setEditOrder(o); setShowPOModal(true); }}><FiEdit2 size={13}/></button>
                            <button className="vmr-icon-btn vmr-del" title="Delete" onClick={() => setConfirm({ type: 'order', item: o })}><FiTrash2 size={13}/></button>
                          </div>
                        </div>

                        {/* Vendor + Project */}
                        <div className="vmr-po-card-meta">
                          <div className="vmr-po-meta-item">
                            <div className="vmr-vendor-avatar vmr-avatar-xs" style={{ background: strColor(o.vendor?.name || '') }}>
                              {o.vendor?.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <span className="vmr-po-meta-lbl">Vendor</span>
                              <span className="vmr-po-meta-val">{o.vendor?.name || '—'}</span>
                            </div>
                          </div>
                          <div className="vmr-po-meta-item">
                            <span style={{ fontSize: 16 }}>🏗️</span>
                            <div>
                              <span className="vmr-po-meta-lbl">Project</span>
                              <span className="vmr-po-meta-val">{o.project?.title || '—'}</span>
                            </div>
                          </div>
                          <div className="vmr-po-meta-item">
                            <FiCalendar size={14} color="#94a3b8"/>
                            <div>
                              <span className="vmr-po-meta-lbl">PO Date</span>
                              <span className="vmr-po-meta-val">{fmt(o.orderDate)}</span>
                            </div>
                          </div>
                          <div className="vmr-po-meta-item">
                            <FiCalendar size={14} color={isDelayed ? '#ef4444' : '#94a3b8'}/>
                            <div>
                              <span className="vmr-po-meta-lbl">Expected</span>
                              <span className="vmr-po-meta-val" style={{ color: isDelayed ? '#ef4444' : undefined }}>{fmt(o.expectedDate) || '—'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="vmr-po-progress-wrap">
                          <div className="vmr-po-progress-bar">
                            <div className="vmr-po-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="vmr-po-progress-pct">{pct}% received</span>
                        </div>

                        {/* Amounts */}
                        <div className="vmr-po-card-amounts">
                          <div className="vmr-po-amt">
                            <span>PO Value</span>
                            <strong>{fmtINR(val)}</strong>
                          </div>
                          <div className="vmr-po-amt vmr-po-amt-recv">
                            <span>Received</span>
                            <strong>{fmtINR(recv)}</strong>
                          </div>
                          <div className="vmr-po-amt vmr-po-amt-pend">
                            <span>Pending</span>
                            <strong>{fmtINR(pend)}</strong>
                          </div>
                        </div>

                        {/* Items count */}
                        <div className="vmr-po-card-footer">
                          <span>{o.items?.length || 0} item{o.items?.length !== 1 ? 's' : ''}</span>
                          <button className="vmr-po-view-btn" onClick={() => setViewOrder(o)}>View Details →</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

      </div>

      {/* ── Confirm dialog ── */}
      {confirm && (
        <div className="vmr-overlay" onClick={() => setConfirm(null)}>
          <div className="vmr-modal" style={{ maxWidth: 380, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🗑️</div>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>{confirm.type === 'vendor' ? 'Delete Vendor' : 'Delete Purchase Order'}</h3>
            <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>
              {confirm.type === 'vendor' ? `Delete "${confirm.item.name}"? This cannot be undone.` : `Delete PO ${confirm.item.poNumber}? This cannot be undone.`}
            </p>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
              <button className="vmr-btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="vmr-btn-primary" style={{ flex: 1, background: '#dc2626' }} onClick={doConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}

      </div>{/* /page */}

      {/* Modals */}
      {showVendorModal && (
        <VendorModal vendor={editVendor} onClose={() => { setShowVendorModal(false); setEditVendor(null); }}
          onSaved={v => { setVendors(p => editVendor ? p.map(x => x._id === v._id ? v : x) : [v, ...p]); setShowVendorModal(false); setEditVendor(null); }} />
      )}
      {showPOModal && (
        <POModal order={editOrder} vendors={vendors} projects={projects.filter(p => plannedProjectIds.has(String(p._id)))}
          onClose={() => { setShowPOModal(false); setEditOrder(null); }}
          onSaved={o => { setOrders(p => editOrder ? p.map(x => x._id === o._id ? o : x) : [o, ...p]); setShowPOModal(false); setEditOrder(null); }} />
      )}
      {viewOrder && <POViewModal order={viewOrder} onClose={() => { setViewOrder(null); loadAll(); }}
        onUpdated={updated => { setOrders(prev => prev.map(o => o._id === updated._id ? updated : o)); setViewOrder(updated); }} />}
      </>
    </Layout>
  );
}

/* ── color from vendor name ── */
const COLORS = ['#2563eb','#7c3aed','#db2777','#ea580c','#16a34a','#0891b2','#9333ea','#dc2626'];
function strColor(str) { let h = 0; for (let c of str) h = c.charCodeAt(0) + ((h << 5) - h); return COLORS[Math.abs(h) % COLORS.length]; }

/* ── Donut chart ── */
function DonutChart({ orders }) {
  const total     = orders.length || 1;
  const received  = orders.filter(o => o.status === 'received').length;
  const partial   = orders.filter(o => o.status === 'partial').length;
  const ordered   = orders.filter(o => o.status === 'sent').length;
  const slices = [
    { color: '#16a34a', pct: received / total },
    { color: '#f59e0b', pct: partial  / total },
    { color: '#2563eb', pct: ordered  / total },
  ];
  const R = 40, cx = 56, cy = 56;
  let paths = [], start = -Math.PI / 2;
  for (const s of slices) {
    const angle = s.pct * 2 * Math.PI;
    if (angle < 0.01) continue;
    const end = start + angle;
    const x1 = cx + R * Math.cos(start), y1 = cy + R * Math.sin(start);
    const x2 = cx + R * Math.cos(end),   y2 = cy + R * Math.sin(end);
    paths.push(<path key={s.color} d={`M${cx},${cy} L${x1},${y1} A${R},${R} 0 ${angle > Math.PI ? 1 : 0},1 ${x2},${y2} Z`} fill={s.color} />);
    start = end;
  }
  return (
    <div className="vmr-donut-wrap">
      <svg viewBox="0 0 112 112" width="120" height="120">
        {orders.length === 0 ? <circle cx="56" cy="56" r="40" fill="#f1f5f9" /> : paths}
        <circle cx="56" cy="56" r="26" fill="#fff" />
        <text x="56" y="53" textAnchor="middle" fontSize="13" fontWeight="700" fill="#0f172a">{orders.length}</text>
        <text x="56" y="64" textAnchor="middle" fontSize="7" fill="#94a3b8">Total POs</text>
      </svg>
      <div className="vmr-donut-legend">
        {[
          { color: '#16a34a', label: 'Fully Received',     count: received, pct: Math.round(received/total*100) },
          { color: '#f59e0b', label: 'Partially Received',  count: partial,  pct: Math.round(partial/total*100) },
          { color: '#2563eb', label: 'Ordered (Not Rec.)',  count: ordered,  pct: Math.round(ordered/total*100) },
        ].map((l,i) => (
          <div key={i} className="vmr-legend-item">
            <span className="vmr-legend-dot" style={{ background: l.color }} />
            <span className="vmr-legend-label">{l.label}</span>
            <span className="vmr-legend-count">{l.count} ({l.pct}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Vendor performance bar chart (SVG) ── */
function VendorPerfChart({ vendors, orders }) {
  const data = vendors.slice(0, 6).map(v => {
    const s = vendorStats(v, orders);
    return { name: v.name?.split(' ')[0] || '?', onTime: s.onTimePct, delay: parseFloat(s.avgDelay) || 0 };
  });
  if (!data.length) return <div className="vmr-empty" style={{ padding: '2rem' }}>No data</div>;
  const maxDelay = Math.max(...data.map(d => d.delay), 1);
  return (
    <div className="vmr-perf-chart">
      <div className="vmr-perf-legend">
        <span><span className="vmr-legend-dot" style={{ background: '#2563eb', display:'inline-block',width:10,height:10,borderRadius:2,marginRight:4 }} />On-Time Delivery %</span>
        <span><span className="vmr-legend-dot" style={{ background: '#f59e0b', display:'inline-block',width:10,height:10,borderRadius:'50%',marginRight:4 }} />Avg. Delivery Delay (Days)</span>
      </div>
      <svg viewBox={`0 0 ${data.length * 60} 100`} width="100%" height="100">
        {data.map((d, i) => {
          const x = i * 60 + 10;
          const barH = (d.onTime / 100) * 70;
          const dotY = 80 - (d.delay / maxDelay) * 60;
          return (
            <g key={i}>
              <rect x={x} y={80 - barH} width={28} height={barH} fill="#2563eb" rx="3" opacity="0.85" />
              <circle cx={x + 14} cy={dotY} r="4" fill="#f59e0b" />
              {i > 0 && (
                <line
                  x1={(i-1)*60+24} y1={80 - (data[i-1].delay / maxDelay)*60}
                  x2={x+14} y2={dotY}
                  stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="3,2"
                />
              )}
              <text x={x+14} y={96} textAnchor="middle" fontSize="7" fill="#64748b">{d.name}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Vendor Modal ── */
function VendorModal({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: vendor?.name || '', contactPerson: vendor?.contactPerson || '',
    phone: vendor?.phone || '', email: vendor?.email || '',
    category: vendor?.category || '', address: vendor?.address || '',
    gstin: vendor?.gstin || '', status: vendor?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.name.trim()) return toast.error('Vendor name is required');
    if (form.phone.trim() && !/^[6-9]\d{9}$/.test(form.phone.trim())) {
      return toast.error('Enter a valid 10-digit mobile number');
    }
    if (form.gstin.trim() && !/^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/.test(form.gstin.trim().toUpperCase())) {
      return toast.error('GSTIN format is invalid. Expected format: 2 digits, 5 letters, 4 digits, 1 letter, 1 digit, "Z", 1 alphanumeric (e.g. 33ABCDE1234F1Z5)');
    }
    setSaving(true);
    try {
      const r = vendor
        ? await API.put(`/purchase/vendors/${vendor._id}`, form)
        : await API.post('/purchase/vendors', form);
      toast.success(vendor ? 'Vendor updated' : 'Vendor added');
      onSaved(r.data);
    } catch { toast.error('Failed to save vendor'); }
    finally { setSaving(false); }
  }

  return (
    <div className="vmr-overlay">
      <div className="vmr-modal" onClick={e => e.stopPropagation()}>
        <div className="vmr-modal-header">
          <h3>{vendor ? 'Edit Vendor' : 'Add Vendor'}</h3>
          <button className="vmr-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="vmr-modal-body">
          <div className="vmr-form-row">
            <div className="vmr-form-group"><label>Vendor Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="vmr-form-group"><label>Contact Person</label><input value={form.contactPerson} onChange={e => set('contactPerson', e.target.value)} /></div>
          </div>
          <div className="vmr-form-row">
            <div className="vmr-form-group"><label>Phone</label><input value={form.phone} maxLength={10} onChange={e => set('phone', e.target.value.replace(/\D/g, ''))} placeholder="10-digit mobile number" /></div>
            <div className="vmr-form-group"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
          </div>
          <div className="vmr-form-row">
            <div className="vmr-form-group"><label>Category</label><input value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Hardware, Plywood…" /></div>
            <div className="vmr-form-group"><label>GSTIN</label><input value={form.gstin} maxLength={15} onChange={e => set('gstin', e.target.value.toUpperCase())} placeholder="e.g. 33ABCDE1234F1Z5" /></div>
          </div>
          <div className="vmr-form-group"><label>Address</label><textarea rows={2} value={form.address} onChange={e => set('address', e.target.value)} /></div>
          <div className="vmr-form-group" style={{ maxWidth: 160 }}>
            <label>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="vmr-modal-footer">
          <button className="vmr-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="vmr-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : vendor ? 'Update' : 'Add Vendor'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── PO Modal ── */
function POModal({ order, vendors, projects, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor: order?.vendor?._id || order?.vendor || '',
    project: order?.project?._id || order?.project || '',
    orderDate: order?.orderDate?.slice(0,10) || new Date().toISOString().slice(0,10),
    expectedDate: order?.expectedDate?.slice(0,10) || '',
    notes: order?.notes || '',
    status: order?.status || 'sent',
  });
  const [items, setItems] = useState(
    order?.items?.length
      ? order.items.map(i => ({ materialName: i.materialName, orderedQty: i.orderedQty, receivedQty: i.receivedQty || 0, unit: i.unit || 'Nos', rate: i.rate || '' }))
      : [{ materialName: '', orderedQty: '', receivedQty: 0, unit: 'Nos', rate: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [extraProjects, setExtraProjects] = useState([]); // additional project IDs to merge materials from
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addItem = () => setItems(p => [...p, { materialName: '', orderedQty: '', receivedQty: 0, unit: 'Nos', rate: '' }]);
  const NUMERIC_ITEM_FIELDS = ['orderedQty', 'receivedQty', 'rate'];
  const setItem = (i, k, v) => {
    if (NUMERIC_ITEM_FIELDS.includes(k) && v !== '' && +v < 0) v = '0';
    setItems(p => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  };
  const removeItem = i => setItems(p => p.filter((_, idx) => idx !== i));

  async function loadFromMaterialPlan() {
    if (!form.project) return toast.error('Select a project first');
    const projectIds = [form.project, ...extraProjects.filter(id => id !== form.project)];
    setLoadingPlan(true);
    try {
      const plans = await Promise.all(projectIds.map(id => API.get(`/material-planning/${id}`).catch(() => null)));
      // Consolidate by material name — the same material can appear under multiple
      // categories/sub-categories/projects, so sum quantities and keep the highest rate seen.
      const consolidated = new Map();
      plans.forEach(r => {
        const cats = r?.data?.categories || [];
        const addMaterial = m => {
          if (!m.materialName) return;
          const key = m.materialName.trim().toLowerCase();
          const existing = consolidated.get(key);
          if (existing) {
            existing.orderedQty = (+existing.orderedQty || 0) + (+m.requiredQty || 0);
            existing.rate = Math.max(+existing.rate || 0, +m.rate || 0);
          } else {
            consolidated.set(key, {
              materialName: m.materialName,
              orderedQty: m.requiredQty || 0,
              receivedQty: 0,
              unit: m.unit || 'Nos',
              rate: m.rate || 0,
            });
          }
        };
        cats.forEach(cat => {
          (cat.materials || []).forEach(addMaterial);
          (cat.subCategories || []).forEach(sub => (sub.materials || []).forEach(addMaterial));
        });
      });
      const planItems = Array.from(consolidated.values());
      if (!planItems.length) return toast.warn('No materials found in the selected project(s) material plan');
      setItems(planItems);
      toast.success(`Loaded ${planItems.length} item(s) from ${projectIds.length} project(s)`);
    } catch { toast.error('Failed to load material plan'); }
    finally { setLoadingPlan(false); }
  }

  async function save() {
    if (!form.vendor) return toast.error('Select a vendor');
    if (!items[0]?.materialName) return toast.error('Add at least one item');
    if (items.some(i => NUMERIC_ITEM_FIELDS.some(k => +(i[k]) < 0))) {
      return toast.error('Negative values are not allowed');
    }
    setSaving(true);
    try {
      const payload = { ...form, items: items.map(i => ({ ...i, orderedQty: Number(i.orderedQty), receivedQty: Number(i.receivedQty || 0), rate: Number(i.rate) })) };
      const r = order ? await API.put(`/purchase/orders/${order._id}`, payload) : await API.post('/purchase/orders', payload);
      toast.success(order ? 'PO updated' : `PO ${r.data.poNumber} created`);
      onSaved(r.data);
    } catch { toast.error('Failed to save PO'); }
    finally { setSaving(false); }
  }

  const totalVal = items.reduce((s, i) => s + (Number(i.orderedQty)||0) * (Number(i.rate)||0), 0);

  return (
    <div className="vmr-overlay">
      <div className="vmr-modal vmr-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="vmr-modal-header">
          <h3>{order ? 'Edit Purchase Order' : 'Create Purchase Order'}{order ? ` — ${order.poNumber}` : ''}</h3>
          <button className="vmr-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="vmr-modal-body">
          <div className="vmr-form-row">
            <div className="vmr-form-group">
              <label>Vendor *</label>
              <ProjectSearchSelect
                projects={vendors.map(v => ({ _id: v._id, title: v.name }))}
                value={form.vendor}
                objectMode={false}
                onChange={id => set('vendor', id || '')}
                placeholder="Select Vendor…"
              />
            </div>
            <div className="vmr-form-group">
              <label>Project</label>
              <ProjectSearchSelect
                projects={projects}
                value={form.project}
                objectMode={false}
                onChange={id => { set('project', id || ''); setExtraProjects(p => p.filter(x => x !== id)); }}
                placeholder="Select Project…"
              />
              {form.project && (
                <div style={{ position: 'relative', marginTop: '.4rem' }}>
                  <button
                    type="button"
                    className="vmr-btn-ghost vmr-sm"
                    onClick={() => setShowProjectPicker(v => !v)}
                    style={{ fontSize: '.72rem' }}
                  >
                    + Add more projects{extraProjects.length ? ` (${extraProjects.length} added)` : ''}
                  </button>
                  {showProjectPicker && (
                    <div className="vmr-modal" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, width: 280, maxHeight: 240, overflowY: 'auto', padding: '.5rem', boxShadow: '0 8px 24px rgba(0,0,0,.15)' }}>
                      {projects.filter(p => p._id !== form.project).map(p => (
                        <label key={p._id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.3rem .25rem', fontSize: '.8rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={extraProjects.includes(p._id)}
                            onChange={e => setExtraProjects(prev => e.target.checked ? [...prev, p._id] : prev.filter(id => id !== p._id))}
                          />
                          {p.title}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="vmr-form-group">
              <label>Status</label>
              <ProjectSearchSelect
                projects={Object.entries(PO_STATUS).map(([k, v]) => ({ _id: k, title: v.label }))}
                value={form.status}
                objectMode={false}
                onChange={id => set('status', id || 'draft')}
                placeholder="Select Status…"
              />
            </div>
          </div>
          <div className="vmr-form-row">
            <div className="vmr-form-group"><label>PO Date</label><input type="date" value={form.orderDate} onChange={e => set('orderDate', e.target.value)} /></div>
            <div className="vmr-form-group"><label>Expected Delivery</label><input type="date" value={form.expectedDate} onChange={e => set('expectedDate', e.target.value)} /></div>
            <div className="vmr-form-group"><label>Notes</label><input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes…" /></div>
          </div>
          <div className="vmr-items-heading">
            <h4>Items</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {form.project && (
                <button
                  type="button"
                  className="vmr-btn-ghost vmr-sm"
                  onClick={loadFromMaterialPlan}
                  disabled={loadingPlan}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#2563eb', borderColor: '#bfdbfe', background: '#eff6ff' }}
                >
                  <FiPackage size={13} />
                  {loadingPlan ? 'Loading…' : extraProjects.length ? `Load from Material Plan (${1 + extraProjects.length} projects)` : 'Load from Material Plan'}
                </button>
              )}
              <span className="vmr-total-preview">Total: {fmtINR(totalVal)}</span>
            </div>
          </div>
          <div className="vmr-table-wrap">
            <table className="vmr-items-table">
              <thead><tr><th>Material Name</th><th>Ordered Qty</th><th>Received Qty</th><th>Rate (₹)</th><th>Amount (₹)</th><th></th></tr></thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i}>
                    <td><input value={item.materialName} onChange={e => setItem(i,'materialName',e.target.value)} placeholder="Material name" /></td>
                    <td><input type="number" min="0" value={item.orderedQty} onChange={e => setItem(i,'orderedQty',e.target.value)} /></td>
                    <td><input type="number" min="0" value={item.receivedQty} onChange={e => setItem(i,'receivedQty',e.target.value)} /></td>
                    <td><input type="number" min="0" value={item.rate} onChange={e => setItem(i,'rate',e.target.value)} /></td>
                    <td className="vmr-td-val">{fmtINR((Number(item.orderedQty)||0)*(Number(item.rate)||0))}</td>
                    <td>{items.length > 1 && <button className="vmr-remove-item" onClick={() => removeItem(i)}><FiX size={11}/></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="vmr-btn-ghost vmr-sm vmr-mt" onClick={addItem}><FiPlus size={12}/> Add Row</button>
        </div>
        <div className="vmr-modal-footer">
          <button className="vmr-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="vmr-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : order ? 'Update PO' : 'Create PO'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── PO View Modal ── */
function POViewModal({ order: initialOrder, onClose, onUpdated }) {
  const [order, setOrder] = React.useState(initialOrder);
  const [recvQty, setRecvQty] = React.useState(() =>
    Object.fromEntries((initialOrder.items||[]).map(it => [it._id, it.receivedQty||0]))
  );
  const [saving, setSaving] = React.useState(null);

  const total    = (order.items||[]).reduce((s,i) => s + (i.orderedQty||0)*(i.rate||0), 0);
  const received = (order.items||[]).reduce((s,i) => s + (i.receivedQty||0)*(i.rate||0), 0);

  async function saveReceive(item) {
    const qty = Number(recvQty[item._id]);
    if (qty < 0) return toast.error('Received quantity cannot be negative');
    if (qty > item.orderedQty) return toast.error(`Received quantity cannot exceed ordered quantity (${item.orderedQty})`);
    setSaving(item._id);
    try {
      const r = await API.put(`/purchase/orders/${order._id}/receive/${item._id}`, { receivedQty: qty });
      setOrder(r.data);
      setRecvQty(Object.fromEntries((r.data.items||[]).map(it => [it._id, it.receivedQty||0])));
      if (onUpdated) onUpdated(r.data);
      toast.success('Receipt updated');
    } catch { toast.error('Failed to update receipt'); }
    finally { setSaving(null); }
  }

  return (
    <div className="vmr-overlay">
      <div className="vmr-modal vmr-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="vmr-modal-header">
          <h3>Purchase Order — {order.poNumber}</h3>
          <Badge status={order.status} config={PO_STATUS} />
          <button className="vmr-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="vmr-modal-body">
          <div className="vmr-view-meta">
            <div><span>Vendor</span><strong>{order.vendor?.name || '—'}</strong></div>
            <div><span>Project</span><strong>{order.project?.title || '—'}</strong></div>
            <div><span>PO Date</span><strong>{fmt(order.orderDate)}</strong></div>
            <div><span>Expected</span><strong>{fmt(order.expectedDate)}</strong></div>
            {order.notes && <div className="vmr-view-full"><span>Notes</span><strong>{order.notes}</strong></div>}
          </div>

          <div className="vmr-table-wrap">
          <table className="vmr-table vmr-mt">
            <thead>
              <tr>
                <th>#</th><th>Material</th><th>Rate (₹)</th>
                <th>Ordered</th><th>Received</th><th>Pending</th><th>Item Status</th><th>Update Receipt</th>
              </tr>
            </thead>
            <tbody>
              {(order.items||[]).map((item, i) => {
                const pending = Math.max(0, (item.orderedQty||0) - (item.receivedQty||0));
                const iStatus = item.receivedQty >= item.orderedQty ? 'received' : item.receivedQty > 0 ? 'partial' : 'sent';
                const isDirty = Number(recvQty[item._id]) !== (item.receivedQty||0);
                return (
                  <tr key={item._id}>
                    <td className="vmr-td-muted">{i+1}</td>
                    <td><strong>{item.materialName}</strong></td>
                    <td>{fmtINR(item.rate)}</td>
                    <td>{item.orderedQty}</td>
                    <td className="vmr-green">{item.receivedQty||0}</td>
                    <td className={pending > 0 ? 'vmr-orange' : 'vmr-green'}>{pending}</td>
                    <td><Badge status={iStatus} config={PO_STATUS} /></td>
                    <td>
                      <div className="vmr-recv-cell">
                        <input
                          type="number" min={0} max={item.orderedQty}
                          value={recvQty[item._id] ?? 0}
                          onChange={e => {
                            let v = e.target.value;
                            if (v !== '' && +v < 0) v = '0';
                            if (v !== '' && +v > item.orderedQty) v = String(item.orderedQty);
                            setRecvQty(p => ({ ...p, [item._id]: v }));
                          }}
                          className="vmr-recv-input"
                          disabled={item.receivedQty >= item.orderedQty}
                        />
                        <button
                          className={`vmr-recv-btn ${isDirty ? 'vmr-recv-btn-active' : ''}`}
                          onClick={() => saveReceive(item)}
                          disabled={!isDirty || saving === item._id || item.receivedQty >= item.orderedQty}
                          title="Save received quantity"
                        >
                          {saving === item._id ? '…' : <FiCheck size={13}/>}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ textAlign:'right', fontWeight:700, padding:'0.6rem 1rem', color:'#0f172a' }}>Total PO Value</td>
                <td style={{ fontWeight:800, color:'#0f172a', padding:'0.6rem 1rem' }}>{fmtINR(total)}</td>
              </tr>
              <tr>
                <td colSpan={7} style={{ textAlign:'right', fontWeight:700, padding:'0.4rem 1rem', color:'#16a34a' }}>Received Value</td>
                <td style={{ fontWeight:700, color:'#16a34a', padding:'0.4rem 1rem' }}>{fmtINR(received)}</td>
              </tr>
              <tr>
                <td colSpan={7} style={{ textAlign:'right', fontWeight:700, padding:'0.4rem 1rem', color:'#f59e0b' }}>Pending Value</td>
                <td style={{ fontWeight:700, color:'#f59e0b', padding:'0.4rem 1rem' }}>{fmtINR(total - received)}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
        <div className="vmr-modal-footer">
          <button className="vmr-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
